import { CircleAIAppShell } from "@circle-react-uikit/CircleAIAppShell";
import { useCircleAIShellStore } from "@circle-react-uikit/CircleAIAppShell/store/circleAIShellStore";
import { selectActiveConversationPanelSurface } from "@circle-react-uikit/CircleAIAppShell/store/conversationPanelSurfaceSlice";
import { lazyWithRetry } from "@circle-react/helpers/lazyRetry";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { PageLoader } from "@circle-react/pages/PageLoader";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { Suspense, useEffect, useRef } from "react";

import { t } from "@/i18n-js/instance";
import { ConversationHeader } from "@/react/components/CircleAi/Home/ConversationHeader";
import { HANDOFF_CONVERSATION_PARAM } from "@/react/components/CircleAi/intents/handoff";
import { useCircleAiStore } from "@/react/components/CircleAi/useCircleAiStore";
import { PageMetaData } from "@/react/components/Layout/PageMetaData";
import { PAGE_SURFACE } from "@/react/constants/pageSurface";

const CircleAiHome = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "CircleAiHome" */ "@/react/components/CircleAi/Home"
    ),
);

const RecentChatsDrawer = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "CircleAiRecentChatsDrawer" */ "@/react/components/CircleAi/Home/RecentChatsDrawer"
  ).then(m => ({ default: m.RecentChatsDrawer })),
);

export const AiConversationPanel = () => {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const isRecentChatsOpen = useCircleAiStore(s => s.isRecentChatsOpen);
  const setIsRecentChatsOpen = useCircleAiStore(s => s.setIsRecentChatsOpen);
  const conversationPanelState = useCircleAIShellStore(
    s => s.conversationPanelState,
  );
  const SurfacePanel = useCircleAIShellStore(
    selectActiveConversationPanelSurface,
  );
  const isDesignPanelOpen = SurfacePanel !== null;

  const search: { [HANDOFF_CONVERSATION_PARAM]?: string } = useSearch({
    strict: false,
  });
  const handoffConversationId = search[HANDOFF_CONVERSATION_PARAM];
  const navigate = useNavigate();

  // Capture the handoff conversation param at mount — the ref is stable and
  // passed to CircleAiHome as initialConversationId before the param is stripped.
  const initialConversationIdRef = useRef<string | undefined>(
    handoffConversationId,
  );

  useEffect(() => {
    if (!initialConversationIdRef.current) return;
    void navigate({
      to: ".",
      search: prev => ({ ...prev, [HANDOFF_CONVERSATION_PARAM]: undefined }),
      replace: true,
      resetScroll: false,
    });
  }, [navigate]);

  useEffect(() => {
    if (conversationPanelState === "closed") setIsRecentChatsOpen(false);
  }, [conversationPanelState, setIsRecentChatsOpen]);

  useEffect(() => {
    setIsRecentChatsOpen(false);
  }, [pathname, setIsRecentChatsOpen]);

  const wasDesignPanelOpenRef = useRef(false);
  useEffect(() => {
    if (isDesignPanelOpen) {
      wasDesignPanelOpenRef.current = true;
      return;
    }
    if (!wasDesignPanelOpenRef.current) return;
    wasDesignPanelOpenRef.current = false;
    const trigger =
      useCircleAIShellStore.getState().conversationPanelSurfaceTrigger;
    useCircleAIShellStore.setState({ conversationPanelSurfaceTrigger: null });
    const active = document.activeElement;
    if (active && active !== document.body) return;
    trigger?.focus();
  }, [isDesignPanelOpen]);

  // A fullscreen panel stands in for the /ai page, so it owns the document
  // title the page used to set.
  const shouldOwnPageTitle = conversationPanelState === "fullscreen";

  return (
    <>
      {shouldOwnPageTitle && <PageMetaData title={t("circle_ai.page_title")} />}
      <CircleAIAppShell.Layout.ConversationPanel.Header>
        {!isDesignPanelOpen && <ConversationHeader surface="panel" />}
      </CircleAIAppShell.Layout.ConversationPanel.Header>
      <CircleAIAppShell.Layout.ConversationPanel.Body>
        <div className="relative size-full">
          <div
            className={classNames("size-full", {
              invisible: isDesignPanelOpen,
            })}
            inert={isDesignPanelOpen || undefined}
          >
            <Suspense fallback={<PageLoader />}>
              <CircleAiHome
                className="h-full"
                initialConversationId={initialConversationIdRef.current}
                surface="panel"
              />
            </Suspense>
            {isRecentChatsOpen && (
              <Suspense fallback={null}>
                <RecentChatsDrawer />
              </Suspense>
            )}
          </div>
          {isDesignPanelOpen && SurfacePanel && (
            <div
              className={classNames(
                "absolute inset-0 z-10 px-5 py-3",
                PAGE_SURFACE,
              )}
            >
              <Suspense fallback={<PageLoader />}>
                <SurfacePanel />
              </Suspense>
            </div>
          )}
        </div>
      </CircleAIAppShell.Layout.ConversationPanel.Body>
    </>
  );
};
